import { useState } from "react";
import axios from "axios";
import { FileUp, File as FileIcon, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Typography } from "@/components/ui/typography";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import FileInput from "@/components/FileInput";
import BreadcrumbComponent from "@/components/BreadcrumbComponent";

interface FileWithStatus {
  file: File;
  progress: number;
  status: "pending" | "uploading" | "success" | "error";
  error?: string;
}

const FileUploader = () => {
  const [files, setFiles] = useState<FileWithStatus[]>([]);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const newFiles = selectedFiles.map((file) => ({
      file,
      progress: 0,
      status: "pending" as const,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const uploadFile = async (fileData: FileWithStatus) => {
    try {
      // Обновляем статус на uploading
      setFiles((prev) =>
        prev.map((f) => (f.file === fileData.file ? { ...f, status: "uploading" as const } : f))
      );

      // TODO: SECURITY - Add authentication to these endpoints
      // Required backend changes:
      // 1. Update https://tool-1c-to-metabase.poolcrm.ru/api/v1/s3/presigned_url to accept:
      //    - Authorization header (Bearer token)
      //    - apikey header
      //    - usertoken header
      // 2. Update https://nn8n.kirillmoiseev.com/webhook/ugc to accept the same auth headers
      // 3. Validate user authorization on backend before allowing file uploads
      //
      // Once backend is ready, add these headers:
      // const userToken = getCookie("user_token");
      // headers: {
      //   "Content-Type": "application/json",
      //   "Authorization": `Bearer ${userToken}`,
      //   "apikey": import.meta.env.VITE_API_KEY,
      //   "usertoken": userToken,
      // }
      const response = await fetch(
        `https://tool-1c-to-metabase.poolcrm.ru/api/v1/s3/presigned_url`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            filename: fileData.file.name,
            content_type: fileData.file.type,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
      }

      const presignedData = await response.json();

      await axios.put(presignedData.url, fileData.file, {
        headers: {
          "Content-Type": fileData.file.type,
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setFiles((prev) =>
              prev.map((f) => (f.file === fileData.file ? { ...f, progress: percentCompleted } : f))
            );
          }
        },
      });

      // TODO: SECURITY - Add authentication (see comment above)
      await fetch("https://nn8n.kirillmoiseev.com/webhook/ugc", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key: presignedData.key,
        }),
      });

      setFiles((prev) =>
        prev.map((f) => (f.file === fileData.file ? { ...f, status: "success" as const } : f))
      );
    } catch (err) {
      setFiles((prev) =>
        prev.map((f) =>
          f.file === fileData.file ? { ...f, status: "error" as const, error: err.message } : f
        )
      );
    }
  };

  const uploadAllFiles = async () => {
    setUploading(true);
    const pendingFiles = files.filter((f) => f.status === "pending");
    await Promise.all(pendingFiles.map(uploadFile));
    setUploading(false);
  };

  const removeFile = (fileToRemove: File) => {
    setFiles((prev) => prev.filter((f) => f.file !== fileToRemove));
  };

  return (
    <div className="container mx-auto max-w-4xl p-3">
      <BreadcrumbComponent link={"Загрузка файлов"} />
      <div className="mb-6 flex items-center gap-2">
        <FileUp className="h-5 w-5 text-primary" />
        <h2 className="text-xl font-medium text-gray-900">Загрузка файлов</h2>
      </div>
      <Card className="overflow-hidden border-none shadow-none">
        <CardContent className="space-y-6 p-0">
          <div className="group relative">
            <div className="absolute -inset-1 rounded-xl bg-gradient-to-r from-primary/20 to-primary/30 opacity-20 blur-md" />
            <div className="relative mx-auto my-auto flex h-[290px] justify-center rounded-xl border-2 border-dashed border-primary/40 p-8 transition-all hover:border-primary/60">
              <div className="flex flex-col items-center justify-center gap-4">
                <FileInput onChange={handleFileChange} disabled={uploading} multiple />
                <Button onClick={uploadAllFiles} disabled={!files.length || uploading} size="lg">
                  {uploading ? "Загрузка..." : "Загрузить файлы"}
                </Button>
              </div>
            </div>
          </div>

          {files.length > 0 && (
            <div className="space-y-4">
              {files.map((fileData) => (
                <Card key={fileData.file.name} className="bg-muted/50">
                  <CardContent className="space-y-2 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileIcon className="h-5 w-5 text-primary" />
                        <div>
                          <Typography variant="p" className="font-medium">
                            {fileData.file.name}
                          </Typography>
                          <Typography variant="p" className="text-muted-foreground">
                            Размер: {(fileData.file.size / 1024).toFixed(2)} KB
                          </Typography>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFile(fileData.file)}
                        disabled={fileData.status === "uploading"}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>

                    {fileData.status === "uploading" && (
                      <div className="space-y-1">
                        <Progress value={fileData.progress} />
                        <Typography variant="p" className="text-center text-primary">
                          Загружено: {fileData.progress}%
                        </Typography>
                      </div>
                    )}

                    {fileData.status === "success" && (
                      <Typography variant="p" className="text-green-600">
                        Файл успешно загружен!
                      </Typography>
                    )}

                    {fileData.status === "error" && (
                      <Typography variant="p" className="text-destructive">
                        Ошибка: {fileData.error}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FileUploader;
